// client/src/routes/app/documents/$documentId.tsx — SCR-DOC-002 „Detaliu document & versiuni" ·
// DOC-004 (04 §8.10; the file path is §7's own tree, „app/documents/$documentId.tsx").
//
// ONE PAYLOAD FEEDS THE SCREEN. `GET /api/v1/documents/{id}` returns the archive row and its
// whole version history, newest first (`DocumentDetail`), so the header, the facts and the
// version list are one request. The BYTES are a second one, per version, and only for the
// version being looked at — see `DocumentPreview`.
//
// THE VERSION BEING LOOKED AT IS A URL PARAM, for §9.2.3's reason and one more: „v1 is still
// downloadable after v2 exists" is 05 DOC-004's acceptance criterion, and a link to v1 that
// survives a refresh is what makes that provable rather than merely true. `version` defaults to
// the document's latest, which is what someone arriving from the archive means.
//
// DOC-004 PHASE 2 ADDS THAT LINK. SCR-DOC-003 lives at `/app/documents/ocr/$documentId` — a
// route of its own per 04 §7's tree — and this header is where a landlord reaches it from: the
// document is on file, and „citește factura" is the next thing they want to do with it. The
// button is offered for the two kinds the pipeline of 02 §10 reads (an invoice and a receipt)
// and only to a caller holding `Expenses.Write`, which is what both calls behind that screen
// require; it is a LINK and not a call, so nothing is written by looking at it (D-1150).

import { createFileRoute, Link, Navigate, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Icon } from '@/components/icons';
import { DetailHeader } from '@/components/layout/DetailHeader';
import { RequirePermission } from '@/components/layout/route-guards';
import { DescriptionList } from '@/components/ui/DescriptionList';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { invoicesVisible } from '@/config/invoices';
import { DocumentPreview } from '@/features/documents/DocumentPreview';
import { DocumentVersions } from '@/features/documents/DocumentVersions';
import { ExpiryChip } from '@/features/documents/ExpiryChip';
import { isWithdrawnKind } from '@/features/documents/enums';
import { useDocumentExpiryLadder } from '@/features/documents/expiring';
import { useDocument } from '@/features/documents/queries';
import { todayInBucharest } from '@/features/payments/today';
import { formatDate } from '@/lib/format';
import { wireNumber } from '@/lib/wire';
import { useCan } from '@/session';

const DASH = '—';

/** The kinds 02 §10's extractor is written for — everything else has no invoice to read. */
const OCR_READABLE_KINDS = ['INVOICE', 'RECEIPT'] as const;

/** Which version the preview is showing. Absent means „the latest", resolved from the payload. */
const documentDetailSearchSchema = z.object({
  version: z.coerce.number().int().min(1).optional(),
});

export const Route = createFileRoute('/app/documents/$documentId')({
  validateSearch: documentDetailSearchSchema,
  component: DocumentDetailScreen,
});

function DocumentDetailScreen() {
  const { documentId } = Route.useParams();
  const { version } = Route.useSearch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const detail = useDocument(documentId);
  const ladder = useDocumentExpiryLadder();
  // Reading an invoice off the document is withdrawn from view with the supplier invoices
  // (D-2780); its route redirects here.
  const canConfirmOcr = useCan('Expenses.Write') && invoicesVisible();

  if (detail.isPending) return <LoadingState rows={4} />;
  if (detail.isError) {
    return <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />;
  }

  const { document, versions } = detail.data;

  // A document of a kind withdrawn from view (D-2780) — an invoice, a declaration, a recipisă —
  // is left out of every list; an address that names one lands on the list too.
  if (isWithdrawnKind(document.kind)) return <Navigate to="/app/documents" replace />;
  // The URL's version if it exists on this document, else the newest. A `?version=9` typed on a
  // two-version document shows v2 rather than an error: the document is real and so is its file.
  const selected =
    versions.find((candidate) => wireNumber(candidate.versionNo) === version) ?? versions[0];

  return (
    <RequirePermission permission="Documents.Read">
      <DetailHeader
        backTo="/app/documents"
        backLabel={t('documents.detail.backToArchive')}
        glyph="document-text"
        title={document.title}
        subtitle={t(`enums.DocumentKind.${document.kind}`)}
        chips={
          <>
            <ExpiryChip
              expirationDate={document.expirationDate}
              today={todayInBucharest()}
              ladder={ladder}
            />
            {document.isEncrypted ? (
              <span className="chip chip-neutral">{t('documents.detail.encrypted')}</span>
            ) : null}
          </>
        }
        actions={
          canConfirmOcr && (OCR_READABLE_KINDS as readonly string[]).includes(document.kind) ? (
            <Link
              to="/app/documents/ocr/$documentId"
              params={{ documentId }}
              className="btn btn-primary btn-sm"
            >
              <Icon name="receipt" className="h-4 w-4" />
              {t('documents.detail.confirmInvoice')}
            </Link>
          ) : undefined
        }
      />

      <section className="section-card mb-6" aria-label={t('documents.detail.factsTitle')}>
        <DescriptionList
          columns={3}
          items={[
            {
              label: t('documents.columns.entity'),
              value: (
                <>
                  {/* The type, and the id beneath it: the link carries no foreign key and no
                      name (03 §12.2), and nothing on this line resolves thirteen of the
                      seventeen. D-1141. */}
                  <span>{t(`enums.DocumentLinkTarget.${document.linkedEntityType}`)}</span>
                  <span className="block text-xs font-normal break-all text-gray-500">
                    {document.linkedEntityId}
                  </span>
                </>
              ),
            },
            { label: t('documents.detail.issuer'), value: document.issuer ?? DASH },
            { label: t('documents.detail.docNumber'), value: document.docNumber ?? DASH },
            {
              label: t('documents.columns.issueDate'),
              value: document.issueDate == null ? DASH : formatDate(document.issueDate),
            },
            {
              label: t('documents.columns.expiration'),
              value: (
                <ExpiryChip
                  expirationDate={document.expirationDate}
                  today={todayInBucharest()}
                  ladder={ladder}
                />
              ),
            },
            {
              label: t('documents.detail.storage'),
              value: document.isEncrypted
                ? t('documents.detail.encrypted')
                : t('documents.detail.plaintext'),
            },
            ...(document.notes != null && document.notes !== ''
              ? [
                  {
                    label: t('documents.upload.notes'),
                    value: <span className="whitespace-pre-line">{document.notes}</span>,
                    span: 2 as const,
                  },
                ]
              : []),
          ]}
        />
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        {selected === undefined ? (
          // A document whose versions are all gone — which the write path cannot produce, but a
          // GDPR hard erasure can (`DocumentStore.Empty`). The row is readable; the file is not.
          <div className="section-card">
            <p className="text-sm text-gray-500">{t('documents.preview.noVersions')}</p>
          </div>
        ) : (
          <DocumentPreview documentId={document.id} version={selected} />
        )}

        <DocumentVersions
          documentId={document.id}
          versions={versions}
          selectedVersionNo={selected === undefined ? 0 : wireNumber(selected.versionNo)}
          onSelect={(versionNo) => {
            void navigate({ to: '.', search: { version: versionNo }, replace: true });
          }}
        />
      </div>
    </RequirePermission>
  );
}
